// Character sheet overlay — HP/MP bars, faction standing, equipped slots.
// Fetches stats + reputation when opened. Opened by 'C' key or HUD button.

import type { Net } from "../net.js";
import type { ClientStore, ClientState } from "../state.js";
import { assetUrl } from "../asset_loader.js";

export function openCharSheet(host: HTMLElement, net: Net, store: ClientStore): HTMLElement {
  const existing = host.querySelector<HTMLElement>(".char-sheet-overlay");
  if (existing) { existing.remove(); return existing; }

  const overlay = document.createElement("div");
  overlay.className = "char-sheet-overlay";
  host.appendChild(overlay);

  // Refresh stats + reputation + inventory on open
  net.send({ t: "stats" });
  net.send({ t: "reputation" });
  net.send({ t: "inventory" });

  function render(s: ClientState): void {
    const me = s.characters.find((c) => c.id === s.character_id) ?? s.characters[0];
    const stats = s.stats;
    const rep = s.reputation;
    const equipped = s.inventory.filter((i) => i.equipped_slot);
    overlay.innerHTML = `
      <div class="char-sheet" role="dialog" aria-label="Character Sheet">
        <header class="cs-header">
          <img class="cs-portrait" alt="" src="${escape(assetUrl("npc", me?.id ?? 0, me ? { name: me.name, category: me.class } : null))}" />
          <div class="cs-title">
            <h2>${escape(me?.name ?? "Unknown")}</h2>
            <div class="cs-subtitle">Level ${stats?.level ?? me?.level ?? 1} · ${escape(me?.class ?? "")} · ${escape(me?.faction ?? "")} · origin: ${escape(me?.origin ?? "")}</div>
            ${rep?.title ? `<div class="cs-rep-title">${escape(rep.title)}</div>` : ""}
          </div>
          <button id="cs-close" class="cs-close" aria-label="Close">×</button>
        </header>
        <section class="cs-vitals">
          ${renderBar("HP", stats?.hp ?? 0, stats?.hp_max ?? 1, "hp")}
          ${renderBar("MP", stats?.mp ?? 0, stats?.mp_max ?? 1, "mp")}
          ${renderXpBar(stats?.xp ?? 0, stats?.level ?? 1)}
        </section>
        <section class="cs-actions">
          <button id="cs-switch" class="cs-action">Switch Character…</button>
          <button id="cs-logout" class="cs-action danger">Log Out</button>
        </section>
        <section class="cs-row">
          <div class="cs-col">
            <h3>Equipped</h3>
            ${equipped.length === 0 ? `<p class="dim">Nothing equipped.</p>` : `
              <ul class="cs-equipped">
                ${equipped.map((i) => `
                  <li>
                    <img class="cs-slot-icon" alt="" src="${escape(assetUrl("icon", i.template_id, { name: i.name, category: i.category }))}" />
                    <span class="cs-slot-name">${escape(i.equipped_slot ?? "")}:</span>
                    <span class="cs-item-name">${escape(i.name)}</span>
                    <span class="cs-item-qty dim">${i.quality ? "(" + escape(i.quality) + ")" : ""}</span>
                  </li>
                `).join("")}
              </ul>
            `}
          </div>
          <div class="cs-col">
            <h3>Faction Standing</h3>
            ${rep ? `
              <table class="cs-rep">
                <tr><th>Hammer</th><td>${rep.hammer}</td></tr>
                <tr><th>Maleficium</th><td>${rep.maleficium}</td></tr>
                <tr><th>Neutral</th><td>${rep.neutral}</td></tr>
              </table>
            ` : `<p class="dim">Reputation loading…</p>`}
          </div>
        </section>
      </div>
    `;

    overlay.querySelector<HTMLButtonElement>("#cs-close")?.addEventListener("click", () => cleanup());
    overlay.querySelector<HTMLButtonElement>("#cs-switch")?.addEventListener("click", () => {
      net.send({ t: "switch_character" });
      cleanup();
    });
    overlay.querySelector<HTMLButtonElement>("#cs-logout")?.addEventListener("click", () => {
      if (!confirm("Log out of your account?")) return;
      net.send({ t: "logout" });
      store.reset();
      cleanup();
    });
  }

  // Forward-declared so render()'s click handlers can reference it.
  let cleanup: () => void = () => { /* set below */ };
  const unsubscribe = store.subscribe(render);
  render(store.get());
  cleanup = (): void => {
    unsubscribe();
    document.removeEventListener("keydown", onEsc);
    overlay.remove();
    store.closePanel?.();
  };
  function onEsc(ev: KeyboardEvent): void { if (ev.key === "Escape") cleanup(); }
  (overlay as unknown as { __cleanup: () => void }).__cleanup = cleanup;
  overlay.addEventListener("click", (e) => { if (e.target === overlay) cleanup(); });
  document.addEventListener("keydown", onEsc);
  return overlay;
}

function renderBar(label: string, cur: number, max: number, cls: string): string {
  const pct = Math.max(0, Math.min(100, (cur / Math.max(1, max)) * 100));
  return `
    <div class="cs-bar cs-bar-${cls}">
      <div class="cs-bar-label">${label}</div>
      <div class="cs-bar-track"><div class="cs-bar-fill" style="width:${pct.toFixed(1)}%"></div></div>
      <div class="cs-bar-value">${cur} / ${max}</div>
    </div>
  `;
}

function renderXpBar(xp: number, level: number): string {
  // Simple curve: 100 * level xp per level.
  const need = 100 * level;
  const pct = Math.max(0, Math.min(100, (xp / need) * 100));
  return `
    <div class="cs-bar cs-bar-xp">
      <div class="cs-bar-label">XP</div>
      <div class="cs-bar-track"><div class="cs-bar-fill" style="width:${pct.toFixed(1)}%"></div></div>
      <div class="cs-bar-value">${xp} / ${need}</div>
    </div>
  `;
}

function escape(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c]!));
}
